import { normalizeSearchKey } from 'src/shared/utils/searchMatch'

/** Root category id when showing subcategories overlay (no product grid yet). */
export const CATALOG_BROWSE_SEARCH_PARAM = 'browse'

export const parseCategoriesFromUrl = (categoriesParam: string): number[] => {
  if (!categoriesParam) return []
  return categoriesParam
    .split(',')
    .map((id) => parseInt(id.trim(), 10))
    .filter((id) => !isNaN(id))
}

export type CategoryPathRow = {
  id: number
  parent_category_id: number | null
}

/** Walk from a category up to the root; returns `categories` URL param (root → leaf). */
export const buildCategoriesParamFromCategoryId = (
  categoryId: number,
  rows: CategoryPathRow[]
): string | null => {
  const byId = new Map(rows.map((r) => [r.id, r]))
  const path: number[] = []
  let current: number | undefined = categoryId
  const seen = new Set<number>()

  while (current !== undefined) {
    if (seen.has(current)) return null
    seen.add(current)
    const row = byId.get(current)
    if (!row) return null
    path.push(row.id)
    const parent = row.parent_category_id
    current =
      parent !== null && parent !== undefined ? parent : undefined
  }

  return path.reverse().join(',')
}

/** Lowercase fragments used to match user input against `category.title` (incl. `en,sv`). */
export const getCategoryTitleSearchParts = (
  title: string | null | undefined
): string[] => {
  if (!title?.trim()) return []
  const trimmed = title.trim()
  const parts = trimmed
    .split(',')
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean)
  const out = new Set<string>()
  out.add(trimmed.toLowerCase())
  for (const p of parts) out.add(p)
  return Array.from(out)
}

export const categoryTitleMatchesDraft = (
  title: string | null | undefined,
  draftLower: string
): boolean => {
  if (!draftLower) return false
  const q = normalizeSearchKey(draftLower)
  if (!q) return false
  return getCategoryTitleSearchParts(title).some((part) =>
    normalizeSearchKey(part).includes(q)
  )
}

/** Lower score = better (0 = starts with draft, 1 = contains, 999 = no match). */
export const categoryMatchSortScore = (
  title: string | null | undefined,
  draftLower: string
): number => {
  if (!draftLower) return 999
  const q = normalizeSearchKey(draftLower)
  if (!q) return 999
  const parts = getCategoryTitleSearchParts(title)
  let best = 999
  for (const part of parts) {
    const n = normalizeSearchKey(part)
    if (n.startsWith(q)) {
      best = Math.min(best, 0 + n.length * 0.001)
    } else if (n.includes(q)) {
      best = Math.min(best, 1 + n.length * 0.001)
    }
  }
  return best
}
